import { Avatar, ProgressBar } from '@/components/ui';
import { IconCheck, IconClock, IconEdit, IconTrash, IconX } from '@/components/Icons';
import { formatBRL, formatDate } from '@/lib/format';
import { formatCrypto, INSTALLMENT_STATUS_LABELS, SALE_STATUS_LABELS } from '@/lib/crypto';
import type { CryptoInstallment, CryptoSale } from '@/lib/types';
import './crypto.css';

interface CryptoSaleCardProps {
  sale: CryptoSale;
  /** Mostra quem vendeu (visão do administrador). */
  showMember: boolean;
  onPay: (installment: CryptoInstallment) => void;
  onUnpay: (installment: CryptoInstallment) => void;
  onEdit: () => void;
  onInterrupt: () => void;
  onResume: () => void;
  onDelete: () => void;
}

const STATUS_CHIP: Record<CryptoSale['status'], string> = {
  ACTIVE: 'chip chip-primary',
  DONE: 'chip chip-positive',
  INTERRUPTED: 'chip',
};

/** O rodapé do cupom: o que interessa em cada situação. */
function stubFoot(installment: CryptoInstallment): string {
  if (installment.status === 'PAID') return installment.paidBrl ? formatBRL(installment.paidBrl) : 'Paga';
  return INSTALLMENT_STATUS_LABELS[installment.status];
}

function stubLabel(installment: CryptoInstallment, sale: CryptoSale): string {
  const amount = formatCrypto(installment.amount, sale.currency);
  const base = `Parcela ${installment.number}, ${amount}, vence ${formatDate(installment.dueDate)}`;
  if (installment.status === 'PAID') return `${base}. Paga${installment.paidBrl ? `, ${formatBRL(installment.paidBrl)}` : ''}. Desmarcar`;
  if (installment.status === 'CANCELED') return `${base}. Cancelada`;
  return `${base}. ${INSTALLMENT_STATUS_LABELS[installment.status]}. Marcar como paga`;
}

/** Um carnê: a venda no alto e a tira de cupons, um por parcela. Clicar no cupom marca (ou desmarca) a parcela. */
export function CryptoSaleCard({ sale, showMember, onPay, onUnpay, onEdit, onInterrupt, onResume, onDelete }: CryptoSaleCardProps) {
  const { progress } = sale;
  const hasPaid = progress.paidCount > 0;

  return (
    <article className="card carne" aria-labelledby={`carne-${sale.id}`}>
      <div className="carne-head">
        <div style={{ minWidth: 0 }}>
          <div className="carne-title" id={`carne-${sale.id}`}>
            <span>{sale.description}</span>
            <span className={STATUS_CHIP[sale.status]}>{SALE_STATUS_LABELS[sale.status]}</span>
            {progress.overdueCount > 0 ? (
              <span className="chip chip-warning">
                {progress.overdueCount} {progress.overdueCount === 1 ? 'atrasada' : 'atrasadas'}
              </span>
            ) : null}
          </div>
          <div className="carne-meta muted">
            {showMember ? (
              <>
                <Avatar name={sale.member.name} color={sale.member.color} photo={sale.member.photoUrl} size={18} />
                <span>{sale.member.name}</span>
                <span aria-hidden>·</span>
              </>
            ) : null}
            <span>{sale.lead ? `Cliente: ${sale.lead.name}` : 'Sem cliente vinculado'}</span>
            {sale.exchange ? (
              <>
                <span aria-hidden>·</span>
                <span>{sale.exchange}</span>
              </>
            ) : null}
            {sale.note ? (
              <>
                <span aria-hidden>·</span>
                <span className="truncate" style={{ maxWidth: 320 }}>{sale.note}</span>
              </>
            ) : null}
          </div>
        </div>

        <div className="carne-figures">
          <div className="carne-figure">
            <span className="label">Total</span>
            <span className="value num">{formatCrypto(sale.totalAmount, sale.currency)}</span>
          </div>
          <div className="carne-figure">
            <span className="label">Entrou em reais</span>
            <span className="value num is-brl">{formatBRL(progress.paidBrl)}</span>
          </div>
          <div className="carne-figure">
            <span className="label">Falta receber</span>
            <span className="value num">{formatCrypto(progress.remainingAmount, sale.currency)}</span>
          </div>
        </div>
      </div>

      <div className="carne-progress">
        <ProgressBar value={progress.paidCount} max={progress.count} color={sale.status === 'INTERRUPTED' ? 'var(--muted-foreground)' : undefined} />
        <span className="num muted nowrap">
          {progress.paidCount}/{progress.count} {progress.count === 1 ? 'parcela paga' : 'parcelas pagas'}
        </span>
      </div>

      <div className="carne-strip" role="group" aria-label="Parcelas">
        {sale.installments.map((installment) => {
          const canceled = installment.status === 'CANCELED';
          const paid = installment.status === 'PAID';
          return (
            <button
              key={installment.id}
              type="button"
              className="stub"
              data-status={installment.status}
              disabled={canceled}
              aria-label={stubLabel(installment, sale)}
              title={paid && installment.txRef ? `Transação: ${installment.txRef}` : undefined}
              onClick={() => (paid ? onUnpay(installment) : onPay(installment))}
            >
              <span className="stub-top">
                <span>{installment.number}ª · {formatDate(installment.dueDate)}</span>
                <span className="stub-mark" aria-hidden>
                  {paid ? <IconCheck /> : canceled ? <IconX /> : installment.status === 'OVERDUE' ? <IconClock /> : null}
                </span>
              </span>
              <span className="stub-amount num">{formatCrypto(installment.amount, sale.currency)}</span>
              <span className="stub-foot num">{stubFoot(installment)}</span>
            </button>
          );
        })}
      </div>

      <div className="carne-foot">
        <span className="muted">
          {sale.status === 'ACTIVE' && sale.nextDueDate
            ? `Próxima cobrança: ${formatDate(sale.nextDueDate)}`
            : sale.status === 'DONE'
              ? 'Todas as parcelas pagas.'
              : 'Interrompida: as parcelas em aberto foram canceladas.'}
        </span>
        <div className="spacer" />
        {sale.status === 'ACTIVE' ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onInterrupt}>Interromper</button>
        ) : null}
        {sale.status === 'INTERRUPTED' ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onResume}>Retomar</button>
        ) : null}
        {hasPaid ? null : (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onDelete}>
            <IconTrash /> Excluir
          </button>
        )}
        <button type="button" className="btn btn-sm" onClick={onEdit}>
          <IconEdit /> Editar
        </button>
      </div>
    </article>
  );
}
